import { timeToX } from './timeScale'
import type { Column, FlatRow, Placement, RowLayout, TimelineEvent } from './types'

export function indexEventsByRow (events: TimelineEvent[]): Map<string, TimelineEvent[]> {
  const index = new Map<string, TimelineEvent[]>()
  for (const event of events) {
    for (const rowKey of event.rowKeys) {
      let list = index.get(rowKey)
      if (!list) {
        list = []
        index.set(rowKey, list)
      }
      list.push(event)
    }
  }
  for (const list of index.values()) {
    list.sort((a, b) => a.start.getTime() - b.start.getTime() || b.end.getTime() - a.end.getTime())
  }
  return index
}

export interface LayoutOptions {
  columns: Column[]
  cellWidth: number
  laneHeight: number
  rowPadding: number
  minEventWidth?: number
}

export function layoutRows (rows: FlatRow[], index: Map<string, TimelineEvent[]>, opts: LayoutOptions): RowLayout {
  const { columns, cellWidth, laneHeight, rowPadding } = opts
  const minWidth = opts.minEventWidth ?? 4
  const heights: number[] = new Array(rows.length)
  const offsets: number[] = new Array(rows.length)
  const placements = new Map<string, Placement[]>()
  const windowStart = columns.length ? columns[0].start : 0
  const windowEnd = columns.length ? columns[columns.length - 1].end : 0
  let total = 0
  for (let i = 0; i < rows.length; i++) {
    const row = rows[i]
    const events = index.get(row.key) || []
    const laneEnds: number[] = []
    const rowPlacements: Placement[] = []
    for (const event of events) {
      const s = event.start.getTime()
      const e = event.end.getTime()
      if (e <= windowStart || s >= windowEnd) {
        continue
      }
      const left = timeToX(s, columns, cellWidth)
      const exact = timeToX(e, columns, cellWidth) - left
      const width = event.band ? exact : Math.max(exact, minWidth)
      // Lanes are packed by drawn extent so bars widened to minWidth never overlap
      let lane = 0
      if (event.band) {
        laneEnds[0] = Math.max(laneEnds[0] || 0, left + width)
      } else {
        while (lane < laneEnds.length && laneEnds[lane] > left + 0.5) {
          lane++
        }
        laneEnds[lane] = left + width
      }
      rowPlacements.push({
        key: event.id + '@' + row.key,
        event,
        rowKey: row.key,
        lane,
        left,
        width,
        clippedStart: s < windowStart,
        clippedEnd: e > windowEnd
      })
    }
    if (rowPlacements.length) {
      placements.set(row.key, rowPlacements)
    }
    const lanes = Math.max(1, laneEnds.length)
    heights[i] = row.height ?? lanes * laneHeight + rowPadding * 2
    offsets[i] = total
    total += heights[i]
  }
  return { heights, offsets, total, placements }
}

export function rowIndexAt (layout: RowLayout, y: number): number {
  const { offsets, heights } = layout
  let lo = 0
  let hi = offsets.length - 1
  while (lo <= hi) {
    const mid = (lo + hi) >> 1
    if (y < offsets[mid]) {
      hi = mid - 1
    } else if (y >= offsets[mid] + heights[mid]) {
      lo = mid + 1
    } else {
      return mid
    }
  }
  return -1
}

export function visibleRowRange (layout: RowLayout, scrollTop: number, viewportHeight: number, overscanPx: number): [number, number] {
  const count = layout.offsets.length
  if (!count) {
    return [0, -1]
  }
  const top = Math.max(0, scrollTop - overscanPx)
  const bottom = Math.min(layout.total - 1, scrollTop + viewportHeight + overscanPx)
  let first = rowIndexAt(layout, top)
  let last = rowIndexAt(layout, Math.max(0, bottom))
  if (first < 0) {
    first = 0
  }
  if (last < 0) {
    last = count - 1
  }
  return [first, last]
}
